import React, { useState, useEffect, useCallback, useRef } from "react";
import { useDropzone } from "react-dropzone";
import Layout from "../components/Layout";
import config from "../config";
import {
  FileText,
  Upload,
  Link,
  FolderOpen,
  X,
  CheckCircle,
  AlertCircle,
  Loader,
  ExternalLink,
  DollarSign,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import "./PdfAccessibility.css";

const PdfAccessibility = () => {
  // Input mode: "upload", "drive_file", "drive_folder"
  const [inputMode, setInputMode] = useState("upload");

  // Form state
  const [selectedFile, setSelectedFile] = useState(null);
  const [driveUrl, setDriveUrl] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Cost estimation
  const [estimateEnabled, setEstimateEnabled] = useState(true);
  const [estimate, setEstimate] = useState(null);
  const [isEstimating, setIsEstimating] = useState(false);
  const [estimateError, setEstimateError] = useState("");
  const [showFileBreakdown, setShowFileBreakdown] = useState(false);
  const abortRef = useRef(null);

  // Jobs
  const [jobs, setJobs] = useState([]);
  const [isLoadingJobs, setIsLoadingJobs] = useState(false);

  // ---- Dropzone ----
  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      setSelectedFile(acceptedFiles[0]);
      setError("");
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "application/pdf": [".pdf"] },
    multiple: false,
  });

  // ---- Fetch jobs ----
  const fetchJobs = useCallback(async () => {
    try {
      setIsLoadingJobs(true);
      const resp = await fetch(
        `${config.api.baseUrl}${config.api.endpoints.pdfAccessibility.jobs}`,
        { credentials: "include" }
      );
      const data = await resp.json();
      if (data.success) {
        setJobs(data.jobs);
      }
    } catch (err) {
      console.error("Error fetching jobs:", err);
    } finally {
      setIsLoadingJobs(false);
    }
  }, []);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  // Poll while any job is queued or processing
  useEffect(() => {
    const hasRunning = jobs.some(
      (j) => j.status === "queued" || j.status === "processing"
    );
    if (!hasRunning) return;
    const interval = setInterval(fetchJobs, 3000);
    return () => clearInterval(interval);
  }, [jobs, fetchJobs]);

  // ---- Cost estimation ----
  const fetchEstimate = useCallback(async (file, url, mode) => {
    // Cancel any in-flight estimate
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setEstimate(null);
    setEstimateError("");
    setIsEstimating(true);

    try {
      let resp;
      if (mode === "upload" && file) {
        const formData = new FormData();
        formData.append("file", file);
        resp = await fetch(
          `${config.api.baseUrl}${config.api.endpoints.pdfAccessibility.estimate}`,
          { method: "POST", credentials: "include", body: formData, signal: controller.signal }
        );
      } else if (url) {
        resp = await fetch(
          `${config.api.baseUrl}${config.api.endpoints.pdfAccessibility.estimate}`,
          {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ drive_url: url.trim() }),
            signal: controller.signal,
          }
        );
      } else {
        setIsEstimating(false);
        return;
      }
      const data = await resp.json();
      if (data.success) {
        setEstimate(data.estimate);
      } else {
        setEstimateError(data.error || "Estimation failed");
      }
    } catch (err) {
      if (err.name !== "AbortError") {
        setEstimateError("Failed to estimate cost");
      }
    } finally {
      setIsEstimating(false);
    }
  }, []);

  // Auto-trigger estimate when file is selected (upload mode)
  useEffect(() => {
    if (!estimateEnabled || inputMode !== "upload" || !selectedFile) {
      if (inputMode === "upload") setEstimate(null);
      return;
    }
    fetchEstimate(selectedFile, null, "upload");
  }, [selectedFile, estimateEnabled, inputMode, fetchEstimate]);

  // Auto-trigger estimate on Drive URL with debounce
  useEffect(() => {
    if (!estimateEnabled || inputMode === "upload" || !driveUrl.trim()) {
      if (inputMode !== "upload") setEstimate(null);
      return;
    }
    // Validate it looks like a Drive URL before fetching
    if (!driveUrl.includes("drive.google.com")) return;

    const timer = setTimeout(() => {
      fetchEstimate(null, driveUrl, inputMode);
    }, 800);
    return () => clearTimeout(timer);
  }, [driveUrl, estimateEnabled, inputMode, fetchEstimate]);

  // Clear estimate when toggling off
  useEffect(() => {
    if (!estimateEnabled) {
      setEstimate(null);
      setEstimateError("");
      setIsEstimating(false);
      if (abortRef.current) abortRef.current.abort();
    }
  }, [estimateEnabled]);

  const formatTokens = (n) => {
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
    return String(n);
  };

  const submitDisabled =
    isSubmitting ||
    (estimateEnabled && !estimate && !isEstimating) ||
    (estimateEnabled && isEstimating);

  // ---- Submit ----
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsSubmitting(true);

    try {
      let resp;

      if (inputMode === "upload") {
        if (!selectedFile) {
          setError("Please select a PDF file.");
          setIsSubmitting(false);
          return;
        }
        const formData = new FormData();
        formData.append("file", selectedFile);
        resp = await fetch(
          `${config.api.baseUrl}${config.api.endpoints.pdfAccessibility.start}`,
          { method: "POST", credentials: "include", body: formData }
        );
      } else {
        if (!driveUrl.trim()) {
          setError("Please enter a Google Drive URL.");
          setIsSubmitting(false);
          return;
        }
        resp = await fetch(
          `${config.api.baseUrl}${config.api.endpoints.pdfAccessibility.start}`,
          {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ drive_url: driveUrl.trim() }),
          }
        );
      }

      const data = await resp.json();
      if (data.success) {
        setSelectedFile(null);
        setDriveUrl("");
        setEstimate(null);
        fetchJobs();
      } else {
        setError(data.error || "Failed to start job");
      }
    } catch (err) {
      setError(err.message || "Network error");
    } finally {
      setIsSubmitting(false);
    }
  };

  // ---- Cancel ----
  const handleCancel = async (jobId) => {
    try {
      const resp = await fetch(
        `${config.api.baseUrl}${config.api.endpoints.pdfAccessibility.cancel}/${jobId}`,
        { method: "POST", credentials: "include" }
      );
      const data = await resp.json();
      if (!data.success) {
        console.error("Failed to cancel:", data.error);
      }
      fetchJobs();
    } catch (err) {
      console.error("Cancel error:", err);
    }
  };

  // ---- Helpers ----
  const getStatusBadge = (status) => {
    const map = {
      queued: {
        label: "Queued",
        className: "pda-badge-queued",
        Icon: Loader,
        spin: true,
      },
      processing: {
        label: "Processing",
        className: "pda-badge-processing",
        Icon: Loader,
        spin: true,
      },
      completed: {
        label: "Completed",
        className: "pda-badge-completed",
        Icon: CheckCircle,
        spin: false,
      },
      error: {
        label: "Error",
        className: "pda-badge-error",
        Icon: AlertCircle,
        spin: false,
      },
      cancelled: {
        label: "Cancelled",
        className: "pda-badge-cancelled",
        Icon: X,
        spin: false,
      },
    };
    const info = map[status] || { label: status, className: "", Icon: null, spin: false };
    return (
      <span className={`pda-badge ${info.className}`}>
        {info.Icon && (
          <info.Icon size={12} className={info.spin ? "pda-spinner" : ""} />
        )}
        {info.label}
      </span>
    );
  };

  const getInputTypeLabel = (job) => {
    const map = {
      upload: "Upload",
      drive_file: "Drive File",
      drive_folder: "Drive Folder",
    };
    return map[job.input_type] || job.input_type || "Unknown";
  };

  const getProgressPercent = (progress) => {
    if (!progress) return 0;
    if (progress.total_pages && progress.total_pages > 0) {
      // For single-file jobs, use page progress
      if (progress.total_files <= 1) {
        return Math.round(
          (progress.current_page / progress.total_pages) * 100
        );
      }
      // For multi-file jobs, combine file-level and page-level progress
      const completedFileWeight = progress.completed_files || 0;
      const currentFilePageProgress =
        progress.total_pages > 0
          ? progress.current_page / progress.total_pages
          : 0;
      const totalFiles = progress.total_files || 1;
      return Math.round(
        ((completedFileWeight + currentFilePageProgress) / totalFiles) * 100
      );
    }
    if (progress.total_files && progress.total_files > 0) {
      return Math.round(
        ((progress.completed_files || 0) / progress.total_files) * 100
      );
    }
    return 0;
  };

  // ---- Render Job Card ----
  const renderJobCard = (job) => {
    const progress = job.progress || {};
    const percent = getProgressPercent(progress);
    const isActive = job.status === "queued" || job.status === "processing";

    return (
      <div
        key={job.job_id}
        className={`pda-job-card status-${job.status}`}
      >
        {/* Top row: title + badge + cancel */}
        <div className="pda-job-top">
          <div className="pda-job-title">
            <FileText size={16} />
            <span>{job.input_filename || "Untitled"}</span>
          </div>
          <div className="pda-job-right">
            {getStatusBadge(job.status)}
            {isActive && (
              <button
                className="pda-cancel-btn"
                onClick={() => handleCancel(job.job_id)}
              >
                Cancel
              </button>
            )}
          </div>
        </div>

        {/* Meta row */}
        <div className="pda-job-meta">
          <span>{getInputTypeLabel(job)}</span>
          <span>
            {job.created_at
              ? new Date(job.created_at).toLocaleString()
              : ""}
          </span>
        </div>

        {/* Progress */}
        {isActive && progress.current_stage && (
          <div className="pda-progress-stage">{progress.current_stage}</div>
        )}

        {isActive && percent > 0 && (
          <>
            <div className="pda-progress-bar-container">
              <div
                className="pda-progress-bar"
                style={{ width: `${percent}%` }}
              />
            </div>
            <div className="pda-progress-detail">
              {progress.total_files > 1 && (
                <span>
                  File {(progress.completed_files || 0) + 1} of{" "}
                  {progress.total_files}
                  {progress.current_file ? ` (${progress.current_file})` : ""}
                  {" \u2014 "}
                </span>
              )}
              {progress.total_pages > 0 && (
                <span>
                  Page {progress.current_page || 0} of {progress.total_pages}
                </span>
              )}
            </div>
          </>
        )}

        {/* Error */}
        {job.status === "error" && job.error && (
          <div className="pda-job-error">
            <AlertCircle size={14} />
            <span>{job.error}</span>
          </div>
        )}

        {/* Output links */}
        {job.status === "completed" && (
          <div className="pda-output-section">
            {job.output_folder_url && (
              <a
                href={job.output_folder_url}
                target="_blank"
                rel="noopener noreferrer"
                className="pda-output-folder"
              >
                <FolderOpen size={16} />
                Open Output Folder
                <ExternalLink size={14} />
              </a>
            )}
            {job.output_files && job.output_files.length > 0 && (
              <ul className="pda-output-files">
                {job.output_files.map((file, idx) => (
                  <li key={idx}>
                    <a
                      href={file.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="pda-output-file-link"
                    >
                      <FileText size={14} />
                      {file.name}
                      <ExternalLink size={12} />
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    );
  };

  // ---- Main Render ----
  return (
    <Layout>
      <div className="pda-container">
        {/* Header */}
        <div className="pda-header">
          <div className="pda-header-row">
            <FileText size={28} className="pda-header-icon" />
            <h1>PDF Accessibility Remediation</h1>
          </div>
          <p className="pda-subtitle">
            Upload a PDF or provide a Google Drive link to automatically
            remediate ADA/WCAG accessibility issues.
          </p>
        </div>

        {/* Form */}
        <form className="pda-form" onSubmit={handleSubmit}>
          <h3 className="pda-form-title">Start a New Job</h3>

          {/* Mode toggle */}
          <div className="pda-mode-toggle">
            <button
              type="button"
              className={`pda-mode-btn ${inputMode === "upload" ? "active" : ""}`}
              onClick={() => {
                setInputMode("upload");
                setDriveUrl("");
                setError("");
                setEstimate(null);
              }}
            >
              <Upload size={16} />
              Upload PDF
            </button>
            <button
              type="button"
              className={`pda-mode-btn ${inputMode === "drive_file" ? "active" : ""}`}
              onClick={() => {
                setInputMode("drive_file");
                setSelectedFile(null);
                setError("");
                setEstimate(null);
              }}
            >
              <Link size={16} />
              Drive File Link
            </button>
            <button
              type="button"
              className={`pda-mode-btn ${inputMode === "drive_folder" ? "active" : ""}`}
              onClick={() => {
                setInputMode("drive_folder");
                setSelectedFile(null);
                setError("");
                setEstimate(null);
              }}
            >
              <FolderOpen size={16} />
              Drive Folder Link
            </button>
          </div>

          {/* Input area */}
          {inputMode === "upload" && (
            <>
              {!selectedFile ? (
                <div
                  {...getRootProps()}
                  className={`pda-dropzone ${isDragActive ? "active" : ""}`}
                >
                  <input {...getInputProps()} />
                  <Upload size={32} className="pda-dropzone-icon" />
                  <p>
                    Drag and drop a PDF here, or click to browse
                    <small>Only .pdf files are accepted</small>
                  </p>
                </div>
              ) : (
                <div className="pda-selected-file">
                  <div className="pda-selected-file-info">
                    <FileText size={16} />
                    <span>{selectedFile.name}</span>
                  </div>
                  <button
                    type="button"
                    className="pda-remove-file"
                    onClick={() => setSelectedFile(null)}
                  >
                    <X size={16} />
                  </button>
                </div>
              )}
            </>
          )}

          {inputMode === "drive_file" && (
            <input
              type="text"
              className="pda-url-input"
              placeholder="https://drive.google.com/file/d/..."
              value={driveUrl}
              onChange={(e) => setDriveUrl(e.target.value)}
            />
          )}

          {inputMode === "drive_folder" && (
            <input
              type="text"
              className="pda-url-input"
              placeholder="https://drive.google.com/drive/folders/..."
              value={driveUrl}
              onChange={(e) => setDriveUrl(e.target.value)}
            />
          )}

          {error && <div className="pda-error">{error}</div>}

          {/* Cost estimation toggle */}
          <div className="pda-estimate-toggle">
            <label className="pda-toggle-label">
              <span>Estimate cost before submitting</span>
              <div
                className={`pda-toggle-switch ${estimateEnabled ? "on" : ""}`}
                onClick={() => setEstimateEnabled((v) => !v)}
                role="switch"
                aria-checked={estimateEnabled}
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setEstimateEnabled((v) => !v); }}
              >
                <div className="pda-toggle-knob" />
              </div>
            </label>
          </div>

          {/* Estimation loading */}
          {estimateEnabled && isEstimating && (
            <div className="pda-estimate-card pda-estimate-loading">
              <Loader size={16} className="pda-spinner" />
              <span>Estimating cost{inputMode === "drive_folder" ? " (downloading PDFs to count pages)" : ""}...</span>
            </div>
          )}

          {/* Estimation error */}
          {estimateEnabled && estimateError && !isEstimating && (
            <div className="pda-estimate-card pda-estimate-error-card">
              <AlertCircle size={14} />
              <span>{estimateError}</span>
            </div>
          )}

          {/* Estimation result */}
          {estimateEnabled && estimate && !isEstimating && (
            <div className="pda-estimate-card">
              <div className="pda-estimate-header">
                <DollarSign size={18} />
                <span className="pda-estimate-cost">
                  ${estimate.tokens.estimated_cost_usd.toFixed(2)}
                </span>
                <span className="pda-estimate-label">estimated cost</span>
              </div>
              <div className="pda-estimate-details">
                <span>{estimate.total_pages} page{estimate.total_pages !== 1 ? "s" : ""}</span>
                <span className="pda-dot">&middot;</span>
                <span>{estimate.total_images} image{estimate.total_images !== 1 ? "s" : ""}</span>
                <span className="pda-dot">&middot;</span>
                <span>~{formatTokens(estimate.tokens.total)} tokens ({formatTokens(estimate.tokens.input)} in + {formatTokens(estimate.tokens.output)} out)</span>
              </div>
              {estimate.file_count > 1 && (
                <button
                  type="button"
                  className="pda-estimate-expand"
                  onClick={() => setShowFileBreakdown((v) => !v)}
                >
                  {showFileBreakdown ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  {estimate.file_count} files
                </button>
              )}
              {showFileBreakdown && estimate.files && (
                <ul className="pda-estimate-files">
                  {estimate.files.map((f, i) => (
                    <li key={i}>
                      <span className="pda-estimate-fname">{f.name}</span>
                      <span className="pda-estimate-fmeta">{f.pages} pg, {f.images} img</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <button
            type="submit"
            className="pda-submit-btn"
            disabled={submitDisabled}
          >
            {isSubmitting ? (
              <>
                <Loader size={18} className="pda-spinner" />
                Starting...
              </>
            ) : estimateEnabled && isEstimating ? (
              <>
                <Loader size={18} className="pda-spinner" />
                Waiting for estimate...
              </>
            ) : (
              "Start Remediation"
            )}
          </button>
        </form>

        {/* Jobs list */}
        <div className="pda-jobs-section">
          <h2 className="pda-jobs-header">Remediation Jobs</h2>
          {isLoadingJobs && jobs.length === 0 && (
            <p className="pda-loading">Loading jobs...</p>
          )}
          {!isLoadingJobs && jobs.length === 0 && (
            <p className="pda-empty">
              No remediation jobs yet. Upload a PDF or provide a Drive link to
              get started.
            </p>
          )}
          {jobs.length > 0 && (
            <div className="pda-jobs-list">
              {jobs.map((job) => renderJobCard(job))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default PdfAccessibility;
